/**
 * POS 기기 등록/수정 폼 페이지
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { posService } from '../../services/posService';
import { PosDevice } from '../../types/pos';

interface PosDeviceFormData {
  device_name: string;
  device_code: string;
  location?: string;
  device_type?: string;
  connection_info?: string;
  status: 'active' | 'inactive' | 'maintenance';
}

const PosDeviceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deviceId = id && id !== 'new' ? parseInt(id, 10) : 0;
  const isEdit = !!deviceId && deviceId > 0;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<PosDeviceFormData>({
    defaultValues: {
      status: 'active',
    },
  });

  const { data: device, isLoading } = useQuery({
    queryKey: ['posDevice', deviceId],
    queryFn: () => posService.getDevice(deviceId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (device && isEdit) {
      reset({
        device_name: device.device_name,
        device_code: device.device_code,
        location: device.location || '',
        device_type: device.device_type || '',
        connection_info: device.connection_info ? JSON.stringify(device.connection_info, null, 2) : '',
        status: device.status,
      });
    }
  }, [device, isEdit, reset]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<PosDevice>) => {
      const formData = { ...data };
      if (formData.connection_info && typeof formData.connection_info === 'string') {
        try {
          formData.connection_info = JSON.parse(formData.connection_info);
        } catch (e) {
          // JSON 파싱 실패 시 그대로 전송
        }
      }
      return posService.createDevice(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posDevices'] });
      navigate('/admin/pos/devices');
    },
    onError: (error: any) => {
      alert(`등록 실패: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PosDevice>) => {
      const formData = { ...data };
      if (formData.connection_info && typeof formData.connection_info === 'string') {
        try {
          formData.connection_info = JSON.parse(formData.connection_info);
        } catch (e) {
          // JSON 파싱 실패 시 그대로 전송
        }
      }
      return posService.updateDevice(deviceId, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posDevices'] });
      queryClient.invalidateQueries({ queryKey: ['posDevice', deviceId] });
      navigate(`/admin/pos/devices/${deviceId}`);
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.message}`);
    },
  });

  const onSubmit = (data: PosDeviceFormData) => {
    // connection_info가 문자열인 경우 JSON으로 파싱
    let connectionInfo: Record<string, any> | undefined = undefined;
    if (data.connection_info) {
      try {
        connectionInfo = JSON.parse(data.connection_info);
      } catch (e) {
        // JSON 파싱 실패 시 undefined로 설정
        connectionInfo = undefined;
      }
    }

    const formData: Partial<PosDevice> = {
      device_name: data.device_name,
      device_code: data.device_code,
      location: data.location,
      device_type: data.device_type,
      connection_info: connectionInfo,
      status: data.status,
    };

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isEdit ? 'POS 기기 수정' : 'POS 기기 등록'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/pos/devices')}>
            목록으로
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>기기명 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('device_name', { required: '기기명을 입력해주세요.' })}
                      isInvalid={!!errors.device_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.device_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>기기 코드 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('device_code', { required: '기기 코드를 입력해주세요.' })}
                      isInvalid={!!errors.device_code}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.device_code?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>설치 위치</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('location')}
                      placeholder="예: 1층 카페, 2층 레스토랑"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>기기 유형</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('device_type')}
                      placeholder="예: 키오스크, 카드단말기"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>연동 정보 (JSON 형식)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      {...register('connection_info')}
                      placeholder='{"api_key": "xxx", "endpoint": "https://..."}'
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                    <Form.Text className="text-muted">
                      POS 기기 연동에 필요한 정보를 JSON 형식으로 입력하세요.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>상태 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Select
                      {...register('status', { required: '상태를 선택해주세요.' })}
                      isInvalid={!!errors.status}
                    >
                      <option value="active">활성</option>
                      <option value="inactive">비활성</option>
                      <option value="maintenance">점검중</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.status?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => navigate('/admin/pos/devices')}>
                  취소
                </Button>
                <Button variant="primary" type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEdit ? '수정 중...' : '등록 중...'}
                    </>
                  ) : (
                    isEdit ? '수정' : '등록'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PosDeviceForm;

